import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { useCreateCompanySwitchMutation } from 'components/hooks/companySwitchMutations';
import useDocumentVisibilityChange from 'components/hooks/useDocumentVisibilityChange';
import useWindowStorage from 'components/hooks/useWindowStorage';

import FilledButton from 'components/application/buttons/FilledButton';
import ModalBox from 'components/application/ModalBox';

import { useCurrentActor } from 'components/contexts/CurrentActor';
import { useBreadBoard } from 'components/contexts/Toaster';

export default function CurrentCompanySyncChecker() {
  const breadBoard = useBreadBoard();
  const createCompanySwitchMutation = useCreateCompanySwitchMutation();

  const {
    division: {
      attributes: { name: companyName },
      id: companyId,
    },
  } = useCurrentActor();

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [getStore, setStore] = useWindowStorage('currentCompany', {
    store: window.localStorage,
  });

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'currentCompanySyncChecker.components.CurrentCompanySyncChecker',
  });

  useEffect(() => {
    setStore({ companyId, companyName });
  }, [companyId]);

  const handleCompanySwitch = () => {
    createCompanySwitchMutation.mutate(
      { companyId },
      {
        onSuccess: ({ _data, included }) => {
          const newCompany = included.find(
            (inclusion) => inclusion.type === 'company',
          );

          setStore({
            companyId: newCompany.id,
            companyName: newCompany.attributes.name,
          });

          setIsModalOpen(false);
        },
        onError: breadBoard.addInedibleToast,
      },
    );
  };

  useDocumentVisibilityChange((visibilityState) => {
    if (
      window.location.pathname === '/dashboard' ||
      visibilityState !== 'visible'
    ) {
      return;
    }

    if (companyId === getStore().companyId) {
      setIsModalOpen(false);
    } else {
      setIsModalOpen(true);
    }
  });

  return (
    <div className='[&_.modalbox]:tw-fixed [&_.modalbox]:tw-z-[999999]'>
      <ModalBox
        customFooter={
          <div className='tw-flex tw-w-full tw-items-center tw-justify-center'>
            <FilledButton
              className='tw-max-w-full tw-items-center tw-justify-center'
              color='red'
              disabled={createCompanySwitchMutation.isLoading}
              onClick={handleCompanySwitch}
            >
              <div className='tw-line-clamp-1'>
                {i18nT('button', {
                  companyName,
                  interpolation: { escapeValue: false },
                })}
              </div>
            </FilledButton>
          </div>
        }
        isOpen={isModalOpen}
        mode='flexible'
        withFooter={true}
      >
        <div className='tw-border-0 tw-border-b-1 tw-border-solid tw-border-grey-100 tw-p-6'>
          <div className='tw-mb-0 tw-line-clamp-2 tw-text-xl tw-font-semibold tw-tracking-tight tw-text-grey-900'>
            {i18nT('heading', {
              companyName,
              interpolation: { escapeValue: false },
            })}
          </div>
        </div>
        <div className='tw-items-center tw-justify-center tw-p-6'>
          <p className='tw-mb-0'>
            {i18nT('body', {
              companyName,
              interpolation: { escapeValue: false },
              viewingCompanyName: getStore()?.companyName,
            })}
          </p>
        </div>
      </ModalBox>
    </div>
  );
}
